import { createContext, ReactNode, useCallback, useState } from 'react';
import { AppObject } from '../widgets/AppCard';
import { MenuGroup } from '../widgets/LeftMenu/LeftMenuGroup';

interface SettingsMenuContextType {
  groups: MenuGroup[];
  handleLinks: (links: SettingsLink[], apps: AppObject[]) => void;
}

interface SettingsMenuContextProviderProps {
  children: ReactNode;
}

export interface SettingsLink {
  name: string;
  key: string;
  iconName: string;
  group?: string;
}

export const SettingsMenuContext = createContext({} as SettingsMenuContextType);

export function SettingsMenuContextProvider({
  children,
}: SettingsMenuContextProviderProps) {
  const [groups, setGroups] = useState<MenuGroup[]>([]);

  const handleLinks = useCallback(
    (links: SettingsLink[], apps: AppObject[]) => {
      const settingsLinks: SettingsLink[] = [];

      if (apps) {
        const sanitizedApps = apps.map((app) => ({
          name: app.name,
          iconName: app.iconName,
          key:
            app.configpage === 'empty'
              ? app.configpage + crypto.randomUUID()
              : app.configpage,
          group: 'Apps Configuration',
        }));

        sanitizedApps.forEach((app) => {
          if (!settingsLinks.find((link) => link.name === app.name)) {
            settingsLinks.push(app);
          }
        });
      }

      let scopedGroups: MenuGroup[] = [];

      if (links) {
        links.forEach((link) => settingsLinks.push(link));
      }

      settingsLinks.forEach((link) => {
        if (link.group) {
          const groupLink = {
            title: link.name,
            iconName: link.iconName,
            key: link.key,
          };

          const groupExists = scopedGroups.find(
            (group) => group.title === link.group
          );

          if (groupExists) {
            const newGroups = scopedGroups.map((group) => {
              if (group.title === link.group) {
                group.items.push(groupLink);
              }
              return group;
            });
            scopedGroups = newGroups;
          } else {
            const newGroup = {
              title: link.group,
              items: [groupLink],
            };
            scopedGroups.push(newGroup);
          }
        }
      });

      setGroups(scopedGroups);
    },
    []
  );

  return (
    <SettingsMenuContext.Provider value={{ groups, handleLinks }}>
      {children}
    </SettingsMenuContext.Provider>
  );
}
